import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonGroup, Grid, Link, Stack, styled, Typography } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getCustomPlanPricing,
  getWorkspacePaymentInfo,
  IWorkspacePaymentInfo,
  sendTalkToSalesEmail,
  updatePaymentMethodInStripe,
} from 'wisestep-api';
import {
  Button,
  getDaysBetweenDates,
  Item,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsDialog,
} from 'wisestep-base';

import { useWorkspaceSKUContext, useWorkspaceContext } from '../../../context';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import PurchasePlanDrawer from '../components/PurchasePlanDrawer';
import { useCustomPlanStore } from '../store/customPlanStore';
import { IPaymentInfo } from '../types/IPaymentInfo';
import { getPlanRate, getRazorPlanRateByInterval, planFeatures } from '../utils/helper';

import CountrySelectionModal from './CountrySelectionModal';
import ManagePlanButton from './ManagePlanButton';
import NotifyPaymentMethodChange from './NotifyPaymentMethodChange';
import PayementConfirmation from './PayementConfirmation';
import PaymentModal from './PaymentModal';
import PlanFeatureList from './PlanFeatureList';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY ?? '');

type ColorType = {
  bgColor: string;
};

const SwitchButton = styled(Button)(({ bgColor }: ColorType) => ({
  border: '1px solid black',
  borderRadius: '50px',
  color: bgColor === WsColors.TheBlues500 ? WsColors.White : WsColors.GenericRichBlack,
  height: '40px',
  backgroundColor: bgColor,
  '&:hover': {
    backgroundColor: WsColors.TheBlues500,
    color: WsColors.White,
  },
}));

const CurrentPlanButton = styled(Button)(() => ({
  backgroundColor: WsColors.Green100,
  border: `1px solid ${WsColors.Green500}`,
  color: `${WsColors.GenericRichBlack} !important`,
}));

const StyledBox = styled(Box)(({ isProModal }: { isProModal?: boolean }) => ({
  height: isProModal ? 'auto' : '2720px',
  backgroundColor: '#FBFCF8',
  boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
}));

const PaymentTitle = ({
  title,
  pay,
  warn,
  showPlanEnd,
  planExpireDate,
}: {
  title: string;
  pay: string;
  warn: boolean;
  showPlanEnd: boolean;
  planExpireDate?: string | null;
}) => (
  <>
    <Typography
      variant={WsTypoVariants.TITLE}
      sx={{ fontWeight: 'bold', fontSize: '30px' }}
      color={WsTypoColors.PRIMARY}
    >
      {title}
    </Typography>
    <Box mt={2}>
      <Typography
        display="inline"
        variant="h5"
        sx={{ fontWeight: 'bold', fontSize: '30px' }}
        color={warn ? WsTypoColors.RED : WsTypoColors.BLACK}
      >
        {pay}
      </Typography>
      <Typography
        display="inline"
        variant="h6"
        sx={{ fontWeight: 'bold' }}
        color={warn ? WsTypoColors.RED : WsTypoColors.BLACK}
      >
        {title == 'Pro' ? ' /User per month' : ''}
      </Typography>
    </Box>
    {!showPlanEnd && (
      <Box mt={2}>
        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
          {title !== 'Free Trial' ? 'Valid for minimum 02 Users' : 'Valid for limited Users'}
        </WsTypography>
      </Box>
    )}
    {showPlanEnd && planExpireDate && (
      <Box mt={2}>
        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.RED}>
          {getDaysBetweenDates(new Date(), new Date(planExpireDate)) >= 0
            ? getDaysBetweenDates(new Date(), new Date(planExpireDate)) == 0
              ? `Expiring Today`
              : `Expire in ${getDaysBetweenDates(new Date(), new Date(planExpireDate))} days`
            : 'Expired'}
        </WsTypography>
      </Box>
    )}
  </>
);

export interface ICustomPlanPricing {
  clientSecret: string;
  price: string;
  interval: string;
}

const PurchasePlanUI = () => {
  const [paymentMethodChange, setPaymentMethodChange] = useState(false);
  const context = useWorkspaceSKUContext();
  const isTrialPlan = context?.workspacePlan?.planName === 'Trial';
  const isExistingCustomer =
    context?.workspacePlan?.planName === 'Standard' ||
    context?.workspacePlan?.planName === 'Premium';
  const customPlanData = useCustomPlanStore((state) => state.customPrice);
  const enableRazorPayEntBuyBtn = useCustomPlanStore((state) => state.enableRazorPayEntBuyBtn);
  const params = useLocation();
  const workspace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const plans = planFeatures;
  const [open, setOpen] = useState(false);
  const [workspaceBillingInfo, setWorkspaceBillingInfo] = useState<IWorkspacePaymentInfo | null>(
    null
  );
  const [feature, setFeature] = useState<string | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<IPaymentInfo | null>(null);
  const [showPostPaymentModal, setShowPostPaymentModal] = useState(false);
  const [planIndex, setPlanIndex] = useState<number>(0);

  useEffect(() => {
    params.search.indexOf('?success=') > -1
      ? setShowPostPaymentModal(true)
      : setShowPostPaymentModal(false);
  }, [params.search]);

  useEffect(() => {
    if (isEmpty(workspaceBillingInfo)) {
      (async () => {
        const res = await getWorkspacePaymentInfo();
        setWorkspaceBillingInfo(res.data);
      })();
    }
  }, [workspaceBillingInfo]);

  const openModal = useCallback(
    async (country, feature, manageUser, sameStateGST?: boolean) => {
      let customerId = null;
      let customPlanCoupon = null;
      let time = 'month';
      let periodInNumber = 1;
      if (planIndex === 1) {
        time = 'quater';
        periodInNumber = 3;
      } else if (planIndex === 2) {
        time = 'annual';
        periodInNumber = 12;
      }
      const selectedProduct = plans?.filter((data) => data.name === feature);
      if (feature === 'Enterprise' && !enableRazorPayEntBuyBtn) {
        try {
          const customPlanRes = await getCustomPlanPricing();
          customerId = customPlanRes.data.id;
          customPlanCoupon = customPlanRes.data.coupon;
        } catch (e) {
          console.log(e);
        }
      }
      setFeature(null);
      setPaymentInfo({
        rate: getPlanRate(feature, planIndex).slice(1, 3),
        plan: feature,
        time: time,
        totalPrice: parseInt(getPlanRate(feature, planIndex).slice(1, 3)) * periodInNumber,
        productId: selectedProduct
          ? country === 'India'
            ? getRazorPlanRateByInterval(feature, time).toString()
            : selectedProduct[0].productId
          : null,
        customerId: customerId,
        customPlanCoupon: feature === 'Enterprise' ? customPlanData ?? customPlanCoupon : null,
        manual: country === 'India' ? true : false,
        manageUsers: manageUser,
        sameStateGST: sameStateGST ?? false,
      });
    },
    [customPlanData, planIndex, plans, enableRazorPayEntBuyBtn]
  );

  const increaseUser = useCallback(
    async (feature: string) => {
      try {
        const res = await getWorkspacePaymentInfo();
        const sameState = res.data.state === 'Telangana';
        res.data.country === 'India'
          ? openModal('India', feature, true, sameState)
          : openModal('US', feature, true);
      } catch (e) {
        openModal('US', feature, true);
      }
    },
    [openModal]
  );

  useEffect(() => {
    if (params.search.indexOf('?user=add') > -1) {
      (context?.workspacePlan?.planName === 'Pro' ||
        context?.workspacePlan?.planName === 'Enterprise') &&
        increaseUser(context?.workspacePlan?.planName);
    }
  }, [context?.workspacePlan?.planName, increaseUser, params.search]);

  const sendEmailToSales = async () => {
    setOpen(true);
    try {
      const response = await sendTalkToSalesEmail();
      if (response.status === 200) {
        setNotification(success('Contact Request Sent Successfully'));
      }
    } catch (error) {
      window.open('https://www.wisestep.com/contact', '_blank');
    }
  };

  const changePaymentMode = async () => {
    const res = await updatePaymentMethodInStripe();
    const sessionId = res.data;
    const stripe = await stripePromise;
    if (stripe) {
      await stripe.redirectToCheckout({
        sessionId,
      });
    }
  };

  return (
    <>
      {paymentMethodChange && (
        <NotifyPaymentMethodChange onClose={() => setPaymentMethodChange(false)} />
      )}
      {feature && (
        <CountrySelectionModal
          open={true}
          feature={feature}
          handleClose={() => setFeature(null)}
          getCountry={openModal}
          workspaceBillingInfo={workspaceBillingInfo}
          onSave={(data: IWorkspacePaymentInfo) => setWorkspaceBillingInfo(data)}
        />
      )}
      {showPostPaymentModal && (
        <PayementConfirmation
          success={params.search.indexOf('?success=true') > -1 ? true : false}
          updated={params.search.indexOf('&update=true') > -1 ? true : false}
        />
      )}
      {paymentInfo && (
        <PaymentModal
          open={!isEmpty(paymentInfo)}
          onClose={() => setPaymentInfo(null)}
          plan={paymentInfo}
        />
      )}
      <Grid container mt={4} mb={4}>
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Select the Plan that Works Best for You
          </Typography>
          <ButtonGroup
            sx={{ mt: 2, boxShadow: 'none' }}
            variant="contained"
            aria-label="outlined primary button group"
          >
            <SwitchButton
              bgColor={planIndex === 0 ? WsColors.TheBlues500 : WsColors.White}
              onClick={() => setPlanIndex(0)}
            >
              Monthly
            </SwitchButton>
            <SwitchButton
              bgColor={planIndex === 1 ? WsColors.TheBlues500 : WsColors.White}
              onClick={() => setPlanIndex(1)}
            >
              Quarterly
            </SwitchButton>
            <SwitchButton
              bgColor={planIndex === 2 ? WsColors.TheBlues500 : WsColors.White}
              onClick={() => setPlanIndex(2)}
            >
              Annual
            </SwitchButton>
          </ButtonGroup>
        </Grid>
        {(context?.workspacePlan?.planName === 'Pro' ||
          context?.workspacePlan?.planName === 'Enterprise') &&
          workspace.stripeSubscriptionId && (
            <Grid item xs={12} textAlign="center" mt={2}>
              <WsTypography>
                Do you want to update your payment method for your current subscription ?
              </WsTypography>
              <Link onClick={changePaymentMode} sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
                Click here for updating the payment method
              </Link>
            </Grid>
          )}
      </Grid>
      <Grid container spacing={5}>
        {isTrialPlan ? (
          <>
            <Grid item xs={0.3}></Grid>
            <Grid item xs={3.7}>
              {isTrialPlan && (
                <Stack>
                  <CurrentPlanButton disabled>Current Plan</CurrentPlanButton>
                </Stack>
              )}
              <StyledBox>
                <Item
                  sx={{
                    textAlign: 'center',
                    mt: isTrialPlan ? 0 : 4,
                  }}
                >
                  <PaymentTitle
                    title="Free Trial"
                    pay="Free"
                    warn={false}
                    showPlanEnd={isTrialPlan}
                    planExpireDate={context?.expiryDate}
                  />
                  <PlanFeatureList features={plans[0].features} />
                </Item>
              </StyledBox>
            </Grid>
          </>
        ) : (
          <Grid item xs={2.32}></Grid>
        )}
        <Grid item xs={3.7}>
          {context?.workspacePlan?.planName === 'Pro' && (
            <Stack>
              <CurrentPlanButton disabled>Current Plan</CurrentPlanButton>
            </Stack>
          )}
          <StyledBox isProModal={true}>
            <Item
              sx={{
                textAlign: 'center',
                mt: context?.workspacePlan?.planName === 'Pro' ? 0 : 4,
              }}
            >
              <PaymentTitle
                title="Pro"
                pay={getPlanRate('Pro', planIndex)}
                warn={false}
                showPlanEnd={context?.workspacePlan?.planName === 'Pro'}
                planExpireDate={context?.expiryDate}
              />
              <Grid item xs={12} mt={2}>
                {context?.workspacePlan?.planName === 'Pro' ? (
                  <>
                    {context?.expiryDate &&
                      getDaysBetweenDates(new Date(), new Date(context?.expiryDate)) > 0 && (
                        <ManagePlanButton onClick={() => increaseUser('Pro')} text="Add Users" />
                      )}
                  </>
                ) : (
                  <>
                    {workspace.owns &&
                      (isTrialPlan || context?.workspacePlan?.planName === 'Pro') && (
                        <ManagePlanButton onClick={() => setFeature('Pro')} text="Buy Now" />
                      )}
                    {context?.expiryDate &&
                      isExistingCustomer &&
                      getDaysBetweenDates(new Date(), new Date(context?.expiryDate)) <= 0 && (
                        <ManagePlanButton onClick={() => setFeature('Pro')} text="Buy Now" />
                      )}
                  </>
                )}
              </Grid>
              <PlanFeatureList features={plans[1].features} />
            </Item>
          </StyledBox>
        </Grid>
        <Grid item xs={3.7}>
          {context?.workspacePlan?.planName === 'Enterprise' && (
            <Stack>
              <CurrentPlanButton disabled>Current Plan</CurrentPlanButton>
            </Stack>
          )}
          <StyledBox>
            <Item
              sx={{
                textAlign: 'center',
                mt: context?.workspacePlan?.planName === 'Enterprise' ? 0 : 4,
              }}
            >
              <PaymentTitle
                title="Enterprise"
                pay="Custom plan"
                showPlanEnd={context?.workspacePlan?.planName === 'Enterprise'}
                warn={false}
                planExpireDate={context?.expiryDate}
              />
              <Grid mt={2}>
                {context?.workspacePlan?.planName === 'Enterprise' ? (
                  <>
                    {context?.expiryDate &&
                      getDaysBetweenDates(new Date(), new Date(context?.expiryDate)) > 0 && (
                        <ManagePlanButton
                          onClick={() => increaseUser('Enterprise')}
                          text="Add Users"
                        />
                      )}
                  </>
                ) : (
                  <>
                    {workspace.owns &&
                      (isTrialPlan || context?.workspacePlan?.planName === 'Enterprise') && (
                        <>
                          {customPlanData || enableRazorPayEntBuyBtn ? (
                            <ManagePlanButton
                              onClick={() => setFeature('Enterprise')}
                              text="Buy Now"
                            />
                          ) : (
                            <Grid container>
                              <Grid item xs={2}></Grid>
                              <Grid item xs={8}>
                                <Stack>
                                  <CurrentPlanButton onClick={sendEmailToSales}>
                                    Talk to Sales
                                  </CurrentPlanButton>
                                </Stack>
                              </Grid>
                            </Grid>
                          )}
                        </>
                      )}
                    {context?.expiryDate &&
                      isExistingCustomer &&
                      getDaysBetweenDates(new Date(), new Date(context?.expiryDate)) <= 0 && (
                        <>
                          {customPlanData || enableRazorPayEntBuyBtn ? (
                            <ManagePlanButton
                              onClick={() => setFeature('Enterprise')}
                              text="Buy Now"
                            />
                          ) : (
                            <Grid container>
                              <Grid item xs={2}></Grid>
                              <Grid item xs={8}>
                                <Stack>
                                  <CurrentPlanButton onClick={sendEmailToSales}>
                                    Talk to Sales
                                  </CurrentPlanButton>
                                </Stack>
                              </Grid>
                            </Grid>
                          )}
                        </>
                      )}
                  </>
                )}
              </Grid>
              <PlanFeatureList features={plans[2].features} />
            </Item>
          </StyledBox>
          <WsDialog size="small" openDialog={open}>
            <CloseIcon
              sx={{
                cursor: 'pointer',
                marginLeft: '94%',
                marginTop: '1%',
                color: WsColors.TheBlues500,
              }}
              fontSize={'medium'}
              onClick={() => setOpen(false)}
            />
            <PurchasePlanDrawer></PurchasePlanDrawer>
          </WsDialog>
        </Grid>
      </Grid>
    </>
  );
};

export default PurchasePlanUI;
